import { API_CONFIG } from '../config/api';
import { ApiValidationError } from '../types/auth';
import type {
  JournalEntriesParams,
  JournalEntriesResponse,
  JournalEntriesResult,
  JournalEntry,
  JournalEntryResponse,
} from '../types/journalEntry';

const authHeaders = (): Record<string, string> => ({
  Accept: 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const parseErrorResponse = async (response: Response, fallback: string) => {
  const errorData = await response.json().catch(() => ({}));
  const message = errorData.message || fallback;

  if (response.status === 422 && errorData.errors) {
    throw new ApiValidationError(message, errorData.errors);
  }

  throw new Error(message);
};

export const getJournalEntries = async (
  params?: JournalEntriesParams
): Promise<JournalEntriesResult> => {
  const queryParams = new URLSearchParams();

  if (params?.start_date) {
    queryParams.set('start_date', params.start_date);
  }
  if (params?.end_date) {
    queryParams.set('end_date', params.end_date);
  }
  if (params?.account_id) {
    queryParams.set('account_id', String(params.account_id));
  }
  if (params?.reference_transaction_id) {
    queryParams.set('reference_transaction_id', String(params.reference_transaction_id));
  }
  if (params?.is_auto_generated !== undefined) {
    queryParams.set('is_auto_generated', params.is_auto_generated ? '1' : '0');
  }
  if (params?.per_page) {
    queryParams.set('per_page', String(params.per_page));
  }
  if (params?.page) {
    queryParams.set('page', String(params.page));
  }

  const query = queryParams.toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.JOURNAL_ENTRIES.GET_ALL}${
    query ? `?${query}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch journal entries');
  }

  const result: JournalEntriesResponse = await response.json();
  return { entries: result.data, meta: result.meta };
};

export const getJournalEntryById = async (id: number): Promise<JournalEntry> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.JOURNAL_ENTRIES.GET_BY_ID}/${id}`,
    {
      method: 'GET',
      headers: authHeaders(),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch journal entry');
  }

  const result: JournalEntryResponse = await response.json();
  return result.data;
};
